import type { AxiosInstance } from 'axios';

export interface UploadedAttachmentResult {
  id?: string;
  filename?: string;
  originalName?: string;
  mimeType?: string;
  size?: number;
  [key: string]: unknown;
}

export interface DirectUploadOptions {
  /** Field name for multipart fallback (default: 'files') */
  multipartFieldName?: string;
  /** Wrap session/complete responses in { data: ... } (case-suppression API) */
  wrappedResponse?: boolean;
  onFileProgress?: (fileIndex: number, percent: number) => void;
}

interface UploadSessionResponse {
  directUpload: boolean;
  uploadUrl?: string;
  uploadToken?: string;
}

function unwrapSessionPayload(data: unknown, wrappedResponse: boolean): UploadSessionResponse {
  if (wrappedResponse && data && typeof data === 'object' && 'data' in data) {
    return (data as { data: UploadSessionResponse }).data;
  }
  return data as UploadSessionResponse;
}

function unwrapCompletePayload(data: unknown, wrappedResponse: boolean): UploadedAttachmentResult {
  if (wrappedResponse && data && typeof data === 'object' && 'data' in data) {
    const inner = (data as { data: { attachment?: UploadedAttachmentResult } }).data;
    return inner.attachment || (inner as UploadedAttachmentResult);
  }
  if (data && typeof data === 'object' && 'attachment' in data) {
    return (data as { attachment: UploadedAttachmentResult }).attachment;
  }
  return data as UploadedAttachmentResult;
}

async function uploadFileToAzure(
  file: File,
  uploadUrl: string,
  onProgress?: (percent: number) => void
): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', uploadUrl);
    xhr.setRequestHeader('x-ms-blob-type', 'BlockBlob');
    xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');

    xhr.upload.onprogress = (event) => {
      if (!event.lengthComputable || !onProgress) return;
      onProgress(Math.round((event.loaded / event.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
        return;
      }
      reject(new Error(`Blob upload failed with status ${xhr.status}`));
    };

    xhr.onerror = () => reject(new Error('Blob upload failed due to a network error'));
    xhr.send(file);
  });
}

async function uploadViaMultipartProxy(
  api: AxiosInstance,
  basePath: string,
  file: File,
  options: DirectUploadOptions
): Promise<UploadedAttachmentResult> {
  const fieldName = options.multipartFieldName || 'files';
  const formData = new FormData();
  formData.append(fieldName, file);

  const proxyResponse = await api.post(basePath, formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });

  const payload = options.wrappedResponse
    ? (proxyResponse.data as { data?: { attachments?: UploadedAttachmentResult[] } }).data
    : proxyResponse.data;

  const attachments = (payload as { attachments?: UploadedAttachmentResult[] })?.attachments;
  if (attachments?.length) {
    return attachments[0];
  }

  return payload as UploadedAttachmentResult;
}

async function uploadSingleFile(
  api: AxiosInstance,
  basePath: string,
  file: File,
  options: DirectUploadOptions,
  fileIndex: number
): Promise<UploadedAttachmentResult> {
  const sessionResponse = await api.post(`${basePath}/upload-sessions`, {
    originalName: file.name,
    mimeType: file.type || undefined,
    size: file.size,
  });

  const session = unwrapSessionPayload(sessionResponse.data, Boolean(options.wrappedResponse));

  if (session.directUpload && session.uploadUrl && session.uploadToken) {
    try {
      await uploadFileToAzure(file, session.uploadUrl, (percent) => {
        options.onFileProgress?.(fileIndex, percent);
      });

      const completeResponse = await api.post(`${basePath}/complete`, {
        uploadToken: session.uploadToken,
      });

      return unwrapCompletePayload(completeResponse.data, Boolean(options.wrappedResponse));
    } catch {
      // CORS or network failure on direct blob PUT — fall back to backend proxy upload.
      return uploadViaMultipartProxy(api, basePath, file, options);
    }
  }

  return uploadViaMultipartProxy(api, basePath, file, options);
}

/**
 * Upload one or more files using direct Azure Blob upload when available,
 * falling back to multipart proxy upload for local development.
 */
export async function uploadAttachmentsDirect(
  api: AxiosInstance,
  basePath: string,
  files: File[] | FileList,
  options: DirectUploadOptions = {}
): Promise<UploadedAttachmentResult[]> {
  const fileList = Array.from(files);
  const results: UploadedAttachmentResult[] = [];

  for (let index = 0; index < fileList.length; index += 1) {
    const file = fileList[index];
    const result = await uploadSingleFile(api, basePath, file, options, index);
    results.push(result);
  }

  return results;
}
